import { CSSProperties, useState } from "react"

import { Document, fileIcons } from "models/documents"
import { DropArea, getAcceptableFiles } from "components/Files"
import { css, cx } from "@emotion/css"
import {
  deleteAgencyDocument,
  fetchAgencyDocuments,
  uploadAgencyDocument,
} from "api/agencies"
import {
  deleteCampaignDocument,
  deletePlanCreative,
  downloadDocument,
  fetchCampaignDocuments,
  fetchPlanCreatives,
  uploadCampaignDocument,
  uploadPlanCreative,
} from "api/library"
import {
  deleteClientConfigDocument,
  deleteClientDocument,
  fetchClientConfigDocuments,
  fetchClientDocuments,
  uploadClientConfigDocument,
  uploadClientDocument,
} from "api/clients"
import {
  deleteClientgroupsDocument,
  fetchClientgroupsDocuments,
  uploadClientgroupsDocument,
} from "api/clientgroups"
import {
  deleteCompanyDocument,
  fetchCompanyDocuments,
  uploadCompanyDocument,
} from "api/companies"
import {
  deleteSupplierDocument,
  fetchSupplierDocuments,
  uploadSupplierDocument,
} from "api/suppliers"
import {
  deleteSuppliergroupsDocument,
  fetchSuppliergroupsDocuments,
  uploadSuppliergroupsDocument,
} from "api/suppliergroups"
import { queryCache, useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { DocumentUploadModal } from "components/DocumentUploadModal"
import { ReactComponent as DocumentsIcon } from "assets/icon-documents.svg"
import { ReactComponent as DownloadIcon } from "assets/download.svg"
import { EmptyState } from "components/EmptyState"
import IconButton from "@material-ui/core/IconButton"
import { Loading } from "components/Loading"
import { PageType } from "api/types"
import groupBy from "lodash/fp/groupBy"
import { itemStyles } from "./styles"
import prop from "lodash/fp/prop"
import { useAuthenticated } from "providers/A12n"
import { useDropzone } from "react-dropzone"
import { useI18n } from "providers/I18n"
import { ApiError } from "api/http/utils"
import { useSendFeedBack } from "components/FeedbackSnackbar"

export type DocumentListItem = {
  type: PageType | "invoice"
  document: Document
  disabled?: boolean
  onDelete?: (id?: string) => void
  onDownload?: (id: string, name: string, agencyId?: string) => Promise<unknown>
  className?: string
}

export const Item = ({
  type,
  document,
  disabled,
  onDelete,
  onDownload,
  className,
}: DocumentListItem) => {
  const { t } = useI18n()
  const { dateFormatter } = useAuthenticated()
  const [toDelete, setToDelete] = useState(false)

  return toDelete ? (
    <div className={itemStyles.delContainer}>
      <div className={itemStyles.delContainerRow}>
        <div className={itemStyles.column}>
          <div className={cx("BodyBody-2BlackRegular", itemStyles.name)}>
            {t(`${type}.documents.delete.prompt`)}
          </div>
          <div className={cx("BodyBody-xsBlackLight", itemStyles.delName)}>
            {document.name}
          </div>
        </div>
        <ButtonGroup className={itemStyles.buttonGroup}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setToDelete(false)}
          >
            {t(`${type}.documents.delete.cancel`)}
          </Button>
          <Button
            color="destructive"
            variant="contained"
            onClick={() => {
              if (onDelete !== undefined) {
                onDelete(document.id)
                setToDelete(true)
              }
            }}
          >
            {t(`${type}.documents.delete.confirm`)}
          </Button>
        </ButtonGroup>
      </div>
    </div>
  ) : (
    <div
      className={cx(itemStyles.container, className)}
      data-testid="document-item"
    >
      <div className={itemStyles.row}>
        <div className={itemStyles.row}>
          {document.fileType !== undefined ? (
            <img
              src={fileIcons[document.fileType]}
              className={itemStyles.image}
              alt={document.fileType}
            />
          ) : null}
          <div className={itemStyles.column}>
            <div className={cx("BodyBody-2BlackRegular", itemStyles.name)}>
              {document.name}
            </div>
            <div
              className={cx("BodyBody-xsDark-greyRegular", itemStyles.details)}
            >
              {document?.dateCreated > 0 &&
                dateFormatter(document.dateCreated) +
                  " at " +
                  dateFormatter(document.dateCreated, {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
              {!!document.createdByDetails?.username
                ? " • " + document.createdByDetails?.username
                : null}
            </div>
          </div>

          <div className={cx(itemStyles.row, itemStyles.button)}>
            {onDownload && (
              <IconButton
                aria-label="download"
                size="small"
                onClick={() => {
                  if (document.id !== undefined)
                    onDownload(
                      document.id,
                      document.name,
                      document?.agencyId,
                    ).catch((e) => {})
                }}
              >
                <DownloadIcon />
              </IconButton>
            )}
            {disabled ? null : (
              <IconButton
                aria-label="delete"
                size="small"
                onClick={() => setToDelete(true)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </div>
      </div>
      {document.description && (
        <div
          className={cx("BodyBody-2Dark-greyRegular", itemStyles.description)}
        >
          {document.description}
        </div>
      )}
    </div>
  )
}

const cacheKeys = {
  campaign: "campaignDocuments",
  plan: "planCreatives",
  agency: "agencyDocuments",
  client: "clientDocuments",
  supplier: "supplierDocuments",
  suppliergroups: "suppliergroupsDocuments",
  clientgroups: "clientgroupsDocuments",
  company: "companyDocuments",
  config: "clientConfigDocuments",
}

const fetchDocument = {
  campaign: fetchCampaignDocuments,
  plan: fetchPlanCreatives,
  agency: fetchAgencyDocuments,
  client: fetchClientDocuments,
  supplier: fetchSupplierDocuments,
  suppliergroups: fetchSuppliergroupsDocuments,
  clientgroups: fetchClientgroupsDocuments,
  company: fetchCompanyDocuments,
  config: fetchClientConfigDocuments,
}

const uploadDocument = {
  campaign: uploadCampaignDocument,
  plan: uploadPlanCreative,
  agency: uploadAgencyDocument,
  client: uploadClientDocument,
  supplier: uploadSupplierDocument,
  suppliergroups: uploadSuppliergroupsDocument,
  clientgroups: uploadClientgroupsDocument,
  company: uploadCompanyDocument,
  config: uploadClientConfigDocument,
}

const deleteDocument = {
  campaign: deleteCampaignDocument,
  plan: deletePlanCreative,
  agency: deleteAgencyDocument,
  client: deleteClientDocument,
  supplier: deleteSupplierDocument,
  suppliergroups: deleteSuppliergroupsDocument,
  clientgroups: deleteClientgroupsDocument,
  company: deleteCompanyDocument,
  config: deleteClientConfigDocument,
}

const styles = {
  container: css`
    flex: 1;
    width: 100%;
    display: flex;
    margin: 0 auto;
  `,
  docs: css`
    transition: opacity 150ms ease-in-out;
  `,
  docsActionPending: css`
    opacity: 0.5;
    pointer-events: none;
  `,
  docsGroup: css`
    margin-bottom: var(--spacing-xl);
  `,
  area: css`
    width: inherit;
  `,
  emptyStateWidth: css`
    width: 100%;
  `,
  title: css`
    margin-bottom: 20px;
  `,
  list: css`
    --rows: 1;
    --columns: 2;
    display: grid;
    grid-template-columns: repeat(var(--columns), minmax(330px, 1fr));
    grid-template-rows: repeat(var(--rows), 1fr);
    grid-column-gap: var(--spacing-xxl-2);
    grid-auto-flow: column;
  `,
  documents: css`
    max-height: 550px;
    overflow: hidden;
    overflow-y: auto;
  `,
  wrapper: css`
    padding: var(--spacing-xxl-2) 0 0;
  `,
  noDataWrapper: css`
    padding: var(--spacing-m) 0 0 0;
  `,
}

export type DocumentsProps = {
  type: PageType
  disabled?: boolean
  ownerEntityId: string
}

const Documents = (props: DocumentsProps) => {
  const { t } = useI18n()
  const sendFeedback = useSendFeedBack()
  const { type, disabled, ownerEntityId } = props
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [document, setDocument] = useState<Document | undefined>(undefined)
  const [dropStatus, setDropStatus] = useState<
    "idle" | "pending" | "success" | "rejected"
  >("idle")

  const { data: docs, isLoading } = useQuery(
    [cacheKeys[type], ownerEntityId],
    fetchDocument[type],
    { onError: (error: ApiError) => sendFeedback(t(error.message), "error") },
  )

  const hasDocs = docs && docs.length > 0

  const [upload] = useMutation(uploadDocument[type], {
    onSuccess: () => {
      queryCache.invalidateQueries([cacheKeys[type], ownerEntityId])
      setIsModalOpen(false)
      setDocument(undefined)
    },
    onError: (error: ApiError) => sendFeedback(t(error.message), "error"),
  })

  const [remove, { isLoading: deleting }] = useMutation(deleteDocument[type], {
    onSuccess: () => {
      queryCache.invalidateQueries([cacheKeys[type], ownerEntityId])
    },
  })

  const handleFile = async (file: File) => {
    setIsModalOpen(true)
    if (document === undefined) {
      setDocument({
        file,
        name: "",
        ownerEntityId,
        dateCreated: new Date().getTime(),
      })
    } else {
      setDocument(Object.assign(document, { file }))
    }
  }

  const onDropAccepted = (acceptedFiles: File[]) => {
    const [file] = acceptedFiles
    if (file) {
      setDropStatus("pending")
      handleFile(file)
        .then(() => setDropStatus("success"))
        .catch(() => setDropStatus("rejected"))
    }
  }

  const dropzoneState = useDropzone({
    accept: getAcceptableFiles().accept,
    onDropAccepted,
    multiple: false,
    noClick: true,
  })

  const handleCancel = () => {
    setIsModalOpen(false)
    setDocument(undefined)
    setDropStatus("idle")
  }

  const handleDelete = (id?: string) => {
    if (id !== undefined) remove(id)
  }

  const handleSubmit = (document: Document) => {
    upload({
      ownerEntityId,
      file: document.file,
      mediaType: document.mediaType,
      description: document.description,
      documentType: document.documentType,
    })
    setDropStatus("idle")
  }

  const byDocumentType = groupBy(prop("documentType"), docs)
  const byMediaType = groupBy((d) => (d.tags ? d.tags[0] : ""), docs)
  const byCategory = type === "plan" ? byMediaType : byDocumentType

  const hasTwoColumns = Object.keys(byCategory).some(
    (category: string) => byCategory[category]?.length > 1,
  )
  const columnsNo = hasTwoColumns ? 2 : 1

  if (disabled && !hasDocs && !isLoading) {
    return (
      <div className={styles.noDataWrapper}>
        <EmptyState
          icon={<DocumentsIcon />}
          title={t("no.documents")}
          className={styles.emptyStateWidth}
        />
      </div>
    )
  } else if (isLoading) {
    return <Loading />
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {hasDocs ? (
          <div
            className={cx(styles.docs, {
              [styles.docsActionPending]: deleting,
            })}
          >
            <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
              {t("droparea.documents")}
            </h5>
            <div className={styles.documents}>
              {Object.keys(byCategory).map((category: string) => {
                const docsByCategory = byCategory[category].length
                const rowsNo = Math.round(docsByCategory / 2)
                return (
                  <div className={styles.docsGroup} key={category}>
                    {category === "undefined" ? null : (
                      <h4 className="BodyBody-2BlackLight">{category}:</h4>
                    )}
                    <div
                      className={styles.list}
                      style={
                        {
                          "--rows": rowsNo,
                          "--columns": columnsNo,
                        } as CSSProperties
                      }
                    >
                      {byCategory[category].map((doc) => (
                        <Item
                          key={doc.id}
                          document={doc}
                          disabled={disabled}
                          onDelete={handleDelete}
                          onDownload={downloadDocument}
                          type={type}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {!disabled && (
          <div className={styles.area}>
            <DropArea
              documentsLabel={t(`${type}.documents.description`)}
              dropzoneState={dropzoneState}
            />
            <DocumentUploadModal
              type={type}
              open={isModalOpen}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              document={document}
              dropStatus={dropStatus}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export { Documents }
